/** @jsxImportSource @emotion/react */
import PropTypes from 'prop-types';
import { Fragment, useEffect, useRef, useState } from 'react';

import disabilityType from '../../mocks/data/questions/disabilityType';
import idFromString from '../../utils/idFromString';
import filterMenuStyle from '../FiltersMenu/styles';
import { ToolTipIcon } from '../Icons';
import TooltipInfo from '../TooltipInfo';
import useStyles from './styles';

const FilterByDisabilityOptions = ({
  onSave,
  currentValue: cValue,
  onErrorChange,
  disabilityOption,
  filterShowHide,
}) => {
  const styles = useStyles();

  const filterMenuStyles = filterMenuStyle();

  const [currentValue, setCurrentValue] = useState(
    Array.isArray(cValue) ? cValue : []
  );
  const lastSavedValue = useRef(Array.isArray(cValue) ? cValue : []);
  const [{ answers: disabilityTypeOptions }] = disabilityType;
  const [isAllSelected, setIsAllSelected] = useState(
    currentValue.length === 0 ||
      (currentValue.length === 1 && currentValue[0].DisabilityId === 0)
  );

  const hasError = currentValue.length === 0 && disabilityOption === 1;
  // Handle input change
  const handleDisabilityOptionsChange = (answerId, isChecked) => {
    setIsAllSelected(false); // Uncheck "All" if any other option is toggled
    if (isChecked) {
      // Remove DisabilityId: 0 if present
      const updatedValue = currentValue.filter(item => item.DisabilityId !== 0);

      // Add the new DisabilityId if not already present
      if (!updatedValue.some(item => item.DisabilityId === answerId)) {
        setCurrentValue([...updatedValue, { DisabilityId: answerId }]);
      } else {
        setCurrentValue(updatedValue);
      }
    } else {
      // Remove the DisabilityId if unchecked
      setCurrentValue(
        currentValue.filter(item => item.DisabilityId !== answerId)
      );
    }
  };

  const handleAllDisabilityToggle = (answerId, isChecked) => {
    if (!isChecked) {
      // Prevent deselecting "All disability formats"
      return;
    }

    setIsAllSelected(isChecked);
    if (isChecked) {
      setCurrentValue([{ DisabilityId: answerId }]); // Clear all other selections
    } else {
      setCurrentValue([]);
    }
  };
  // Call onSave only after currentValue updates
  useEffect(() => {
    if (currentValue !== lastSavedValue.current) {
      onSave({ DisabilityType: currentValue });
      lastSavedValue.current = currentValue;
    }
  }, [currentValue, onSave]);

  useEffect(() => {
    onErrorChange(hasError); // Notify parent whenever error state changes
  }, [hasError, onErrorChange]);

  useEffect(() => {
    if (currentValue.length === 0) {
      setIsAllSelected(true);
      setCurrentValue([{ DisabilityId: 0 }]);
    }
  }, [currentValue]);

  return (
    <Fragment>
      <div css={filterMenuStyles.filterHead}>
        <h5 css={filterMenuStyles.filterTitle}>
          Disability Football format
          <span>
            <TooltipInfo
              icon={<ToolTipIcon />}
              headTitle="Disability Football format"
            >
              <>
                <div>
                  Select your preferred format(s) of disability football from
                  the list.
                </div>
                <div>
                  For more information about disability football formats, visit
                  England Football:{' '}
                  <a
                    href="https://www.englandfootball.com/play/Disability-Football"
                    target="_blank"
                    rel="noreferrer"
                    ipInfo
                  >
                    https://www.englandfootball.com/play/Disability-Football
                  </a>
                </div>
              </>
            </TooltipInfo>
          </span>
        </h5>
      </div>
      <div css={filterMenuStyles.filterBody}>
        <ul css={styles.listWrapper}>
          <li key={0}>
            <label
              htmlFor="all-disability-formats"
              css={styles.chooseFilter(isAllSelected)}
            >
              <span>All disability formats</span>
              <input
                type="checkbox"
                id="all-disability-formats"
                name="all-disability-formats"
                checked={isAllSelected}
                value={0}
                onChange={e => handleAllDisabilityToggle(0, e.target.checked)}
                tabIndex={filterShowHide ? 0 : -1}
                css={styles.optionCheckbox}
                disabled={isAllSelected} // Prevent manual uncheck
              />
              <span />
            </label>
          </li>

          <li css={styles.divider}>OR</li>

          {disabilityTypeOptions &&
            disabilityTypeOptions.map(({ answerId, value }) => {
              const optionId = idFromString(value);

              // Check if answerId exists in currentValue array
              const isChecked = currentValue?.some(
                item => item.DisabilityId === answerId
              );
              // const isChecked = true;
              return (
                <li key={answerId}>
                  <label
                    htmlFor={optionId}
                    css={styles.chooseFilter(isChecked)}
                    key={answerId}
                  >
                    <span>{value}</span>

                    <input
                      type="checkbox"
                      id={optionId}
                      name={optionId}
                      checked={isChecked}
                      value={answerId}
                      css={styles.optionCheckbox}
                      onChange={e =>
                        handleDisabilityOptionsChange(
                          answerId,
                          e.target.checked
                        )
                      }
                      tabIndex={filterShowHide ? 0 : -1}
                    />
                    <span />
                  </label>
                </li>
              );
            })}
        </ul>
      </div>
    </Fragment>
  );
};

FilterByDisabilityOptions.propTypes = {
  currentValue: PropTypes.array,
  onSave: PropTypes.func,
};

export default FilterByDisabilityOptions;
